import { PLATE_SHAPES, type PlateDef } from '../data/plates';
import { getCustomPictogramSvg } from '../pictograms';

/**
 * A miniature of the actual plate.
 *
 * Recognising a plate at 100km/h is a visual job, so the list shows a picture of
 * the thing rather than a colour chip with a state code on it.
 *
 * Each plate is drawn at its **true aspect ratio** inside a fixed box, so a Euro
 * plate reads as long and low and a motorcycle plate as short and stubby without
 * either needing a caption. The box stays a constant size so the list rows
 * still line up.
 *
 * Australian plates are embossed on a reflective blank with a rolled rim and two
 * mounting holes; those details are what make a small rectangle read as "number
 * plate" rather than "coloured box", so they are drawn rather than implied.
 *
 * NOTE: If a crafted, flat-design SVG exists in `src/pictograms/svg/{id}.svg`,
 * that is rendered directly. Otherwise, it falls back to the procedural version.
 */

interface Props {
  plate: PlateDef;
  /** Width of the bounding box in CSS pixels. */
  width?: number;
}

const BOX_W = 108;
const BOX_H = 46;

/** Keeps the emitted SVG readable, and avoids 1e-15 style float noise. */
const r = (n: number): number => Math.round(n * 100) / 100;

/** Stamped characters are condensed, so long serials lose height slowly. */
function serialFontSize(sample: string, plateHeight: number): number {
  const length = [...sample].length;
  const base =
    length <= 3 ? 0.52 :
    length <= 5 ? 0.46 :
    length <= 7 ? 0.40 :
    length <= 9 ? 0.34 : 0.28;
  return plateHeight * base;
}

/**
 * A very simplified thylacine, as stamped on Tasmanian general issue and used
 * as the character separator on TasPlates Euro plates. At this size it only has
 * to read as a small striped animal, which is also all you get at road speed.
 */
function Thylacine({ x, y, size, fill }: { x: number; y: number; size: number; fill: string }) {
  const s = size / 10;
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} fill={fill} opacity="0.9">
      {/* body + head + tail in one stroke-free silhouette */}
      <path d="M0.6 5.2 C1.4 3.9 3 3.5 4.6 3.6 L6.4 3.7 C7.1 3.1 7.9 2.9 8.6 3.2 L9.6 2.2 L9.4 3.6 C9.9 4.1 9.8 4.8 9.2 5.1 L7.8 5.3 C7.6 6.2 7 6.9 6.1 7.2 L6.3 8.6 L5.4 8.6 L5.2 7.4 L3.4 7.4 L3.2 8.6 L2.3 8.6 L2.4 7.1 C1.4 6.8 0.7 6.1 0.6 5.2 Z" />
      {/* the stripes it is named for */}
      <rect x="3.4" y="4.2" width="0.42" height="2.5" opacity="0.55" fill="#ffffff" />
      <rect x="4.4" y="4.1" width="0.42" height="2.6" opacity="0.55" fill="#ffffff" />
      <rect x="5.4" y="4.2" width="0.42" height="2.4" opacity="0.55" fill="#ffffff" />
    </g>
  );
}

export function PlateMini({ plate, width = 96 }: Props) {
  const customSvg = getCustomPictogramSvg(plate.id);
  if (customSvg) {
    const height = Math.round((width * BOX_H) / BOX_W);
    const preparedSvg = customSvg.replace(/<svg\b([^>]*)>/i, (_match, attrs) => {
      const cleanedAttrs = attrs
        .replace(/\b(width|height|class|className|role|aria-label)="[^"]*"/gi, '')
        .trim();
      return `<svg class="plate-mini" width="${width}" height="${height}" role="img" aria-label="${plate.name} plate" ${cleanedAttrs}>`;
    });

    return (
      <span
        className="plate-mini-frame"
        style={{ display: 'inline-block', lineHeight: 0 }}
        dangerouslySetInnerHTML={{ __html: preparedSvg }}
      />
    );
  }

  const [background, ink] = plate.colours;
  const shape = PLATE_SHAPES[plate.shape];

  // Fit the real plate proportions inside the fixed box.
  const scale = Math.min(BOX_W / shape.w, BOX_H / shape.h);
  const w = r(shape.w * scale);
  const h = r(shape.h * scale);
  const x = r((BOX_W - w) / 2);
  const y = r((BOX_H - h) / 2);

  const hasLogo = plate.logo === 'thylacine';
  const serialSize = r(serialFontSize(plate.sample, h));
  const serialY = r(y + h * (plate.shape === 'euro' ? 0.62 : 0.56));
  const legendY = r(y + h * 0.87);
  const legendSize = r(Math.max(h * 0.15, 4.4));
  const squeeze = plate.legend.length > 16;

  const gradientId = `sheen-${plate.id}`;

  return (
    <svg
      className="plate-mini"
      viewBox={`0 0 ${BOX_W} ${BOX_H}`}
      width={width}
      height={Math.round((width * BOX_H) / BOX_W)}
      role="img"
      aria-label={`${plate.name} plate`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.34" />
          <stop offset="45%" stopColor="#ffffff" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.10" />
        </linearGradient>
      </defs>

      {/* blank */}
      <rect x={x} y={y} width={w} height={h} rx={r(h * 0.13)} fill={background} />
      {/* rolled rim */}
      <rect
        x={r(x + h * 0.055)} y={r(y + h * 0.055)}
        width={r(w - h * 0.11)} height={r(h - h * 0.11)}
        rx={r(h * 0.09)}
        fill="none" stroke={ink} strokeOpacity="0.42" strokeWidth={r(Math.max(h * 0.028, 0.5))}
      />

      <text
        x={r(BOX_W / 2 + (hasLogo ? h * 0.14 : 0))}
        y={serialY}
        textAnchor="middle"
        fill={ink}
        fontSize={serialSize}
        fontWeight="700"
        fontFamily="'Arial Narrow', 'Roboto Condensed', 'Helvetica Neue', Arial, sans-serif"
        letterSpacing={r(w * 0.004)}
      >
        {plate.sample}
      </text>

      {hasLogo && (
        <Thylacine
          x={r(x + w * 0.07)}
          y={r(serialY - serialSize * 0.82)}
          size={r(serialSize * 0.95)}
          fill={ink}
        />
      )}

      <text
        x={BOX_W / 2}
        y={legendY}
        textAnchor="middle"
        fill={ink}
        fillOpacity="0.92"
        fontSize={legendSize}
        fontWeight="600"
        fontFamily="'Arial Narrow', 'Roboto Condensed', 'Helvetica Neue', Arial, sans-serif"
        {...(squeeze
          ? { textLength: r(w - h * 0.5), lengthAdjust: 'spacingAndGlyphs' as const }
          : {})}
      >
        {plate.legend}
      </text>

      {/* mounting holes */}
      <circle cx={r(x + w * 0.19)} cy={r(y + h * 0.17)} r={r(Math.max(h * 0.035, 0.6))}
        fill={ink} fillOpacity="0.28" />
      <circle cx={r(x + w * 0.81)} cy={r(y + h * 0.17)} r={r(Math.max(h * 0.035, 0.6))}
        fill={ink} fillOpacity="0.28" />

      {/* reflective sheen, over everything */}
      <rect x={x} y={y} width={w} height={h} rx={r(h * 0.13)} fill={`url(#${gradientId})`} />
    </svg>
  );
}
